<template>
  <div class="video-player-container" :class="aspectRatioClass">
    <video ref="videoPlayer" class="video-js vjs-default-skin vjs-big-play-centered" controls preload="auto"></video>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-hls';

export default {
  name: 'VideoPlayer',
  props: {
    options: {
      type: Object,
      default() {
        return {};
      }
    },
    src: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      player: null
    };
  },
  computed: {
    // Determine the aspect ratio class based on the video dimensions
    aspectRatioClass() {
      const aspectRatio = this.options.aspectRatio;
      if (!aspectRatio) return 'aspect-landscape'; // Default to landscape

      // Parse aspect ratio string (e.g., "133:100" or "16:9")
      const [width, height] = aspectRatio.split(':').map(num => parseFloat(num));
      const ratio = width / height;

      // Classify the aspect ratio
      if (ratio < 0.8) {
        return 'aspect-portrait';
      } else if (ratio > 1.3) {
        return 'aspect-landscape';
      } else {
        return 'aspect-square';
      }
    },

    // Calculate responsive dimensions based on aspect ratio
    playerDimensions() {
      const aspectRatio = this.options.aspectRatio;
      if (!aspectRatio) return { width: '100%', height: '100%' };

      const [width, height] = aspectRatio.split(':').map(num => parseFloat(num));
      const ratio = width / height;

      // For portrait videos, limit the width and calculate height
      if (ratio < 0.8) {
        return {
          maxWidth: '60vh', // Limit width for portrait videos
          aspectRatio: `${width} / ${height}`
        };
      }

      // For landscape videos, use full container
      return {
        width: '100%',
        height: '100%',
        aspectRatio: `${width} / ${height}`
      };
    }
  },
  mounted() {
    this.initializePlayer();
  },
  beforeUnmount() {
    if (this.player) {
      this.player.dispose();
    }
  },
  watch: {
    src(newSrc, oldSrc) {
      if (this.player && newSrc && newSrc !== oldSrc) {
        // Pause and reset the player first to avoid conflicts
        this.player.pause();
        this.player.reset();

        // Small delay to ensure cleanup is complete
        this.$nextTick(() => {
          // Determine video type based on URL
          const isHLS = newSrc.includes('.m3u8') || newSrc.includes('hls') || newSrc.includes('/hls/');
          const videoType = isHLS ? 'application/x-mpegURL' : 'video/mp4';

          try {
            this.player.src({ src: newSrc, type: videoType });
            this.player.load(); // Load the new source

            // Update dimensions after source change
            this.$nextTick(() => {
              this.updatePlayerDimensions();
            });

            // Only auto-play if the previous video was playing
            // For now, let's not auto-play to avoid conflicts
            // this.player.play();
          } catch (error) {
            console.error('Error changing video source:', error);
          }
        });
      }
    },

    // Watch for options changes (including aspect ratio)
    options: {
      deep: true,
      immediate: true,
      handler(newOptions, oldOptions) {
        if (this.player && newOptions) {
          // Check if aspect ratio changed
          const aspectRatioChanged = !oldOptions || newOptions.aspectRatio !== oldOptions.aspectRatio;

          if (aspectRatioChanged) {
            // Force update of player dimensions
            this.$nextTick(() => {
              this.updatePlayerDimensions();
            });
          }
        }
      }
    }
  },
  methods: {
    initializePlayer() {
      // Determine video type based on URL
      const isHLS = this.src && (this.src.includes('.m3u8') || this.src.includes('hls') || this.src.includes('/hls/'));
      const videoType = isHLS ? 'application/x-mpegURL' : 'video/mp4';

      const videoOptions = {
        responsive: true,
        fluid: true,
        ...this.options,
        sources: [
          {
            src: this.src,
            type: videoType
          }
        ]
      };

      this.player = videojs(this.$refs.videoPlayer, videoOptions, () => {
        this.updatePlayerDimensions();
      });

      // Handle errors
      this.player.on('error', () => {
        const error = this.player.error();
        if (error) {
          // Try to recover from certain errors
          if (error.code === 4) {
            // MEDIA_ERR_SRC_NOT_SUPPORTED
            this.retryWithDifferentType();
          }
        }
      });
    },

    updatePlayerDimensions() {
      if (!this.player) return;

      // Apply the computed dimensions to the player
      const dimensions = this.playerDimensions;
      const playerEl = this.player.el();

      if (playerEl) {
        // Clear existing styles first
        playerEl.style.maxWidth = '';
        playerEl.style.aspectRatio = '';
        playerEl.style.width = '';
        playerEl.style.height = '';

        // Apply new dimensions
        Object.keys(dimensions).forEach(key => {
          playerEl.style[key] = dimensions[key];
        });

        // Force the player to recalculate its dimensions
        this.player.dimensions(playerEl.offsetWidth, playerEl.offsetHeight);

        // Trigger a resize event to ensure proper layout
        setTimeout(() => {
          if (this.player) {
            this.player.trigger('resize');
          }
        }, 50);
      }
    },

    retryWithDifferentType() {
      if (!this.player || !this.src) return;

      // Try the opposite video type
      const currentSrc = this.player.currentSrc() || this.src;
      const wasHLS = currentSrc.includes('.m3u8') || currentSrc.includes('hls') || currentSrc.includes('/hls/');
      const newVideoType = wasHLS ? 'video/mp4' : 'application/x-mpegURL';

      try {
        this.player.src({ src: this.src, type: newVideoType });
        this.player.load();
      } catch (error) {
        // Retry failed, silently continue
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.video-player-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  box-sizing: border-box;
  background-color: transparent;

  // Simplified aspect ratio handling
  &.aspect-portrait {
    max-width: 50vh;
    margin: 0 auto; // Center the portrait video horizontally

    .video-js {
      max-height: 60vh;
    }
  }

  &.aspect-landscape {
    .video-js {
      max-height: 50vh;
    }
  }

  &.aspect-square {
    max-width: 60vh;
    margin: 0 auto; // Center the square video horizontally

    .video-js {
      max-height: 55vh;
    }
  }
}

// Base video player styles
.video-js {
  width: 100% !important;
  height: auto !important;
  object-fit: cover !important;
  border-radius: 8px;
  font-size: 10px;
  color: var(--main-btn-color);
  background-color: transparent !important; // Override VideoJS default black background

  // Override VideoJS default backgrounds
  &.vjs-default-skin {
    background-color: transparent !important;
  }

  // Ensure video element itself has no background
  video {
    background-color: transparent !important;
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    max-height: 45vh !important;
  }

  @media (max-width: 480px) {
    max-height: 35vh !important;
  }
}

// Theme-based control styling
.video-js .vjs-control-bar,
.video-js .vjs-big-play-button,
.video-js .vjs-menu-button .vjs-menu-content {
  background-color: var(--main-btn-color);
}

.video-js .vjs-slider {
  background-color: var(--main-btn-color);
}

.video-js .vjs-volume-level,
.video-js .vjs-play-progress,
.video-js .vjs-slider-bar {
  background: var(--terziary-btn-color);
}

.video-js .vjs-load-progress {
  background: var(--terziary-btn-color);
}

.video-js .vjs-load-progress div {
  background: var(--terziary-btn-color);
}

// Big play button
.vjs-default-skin .vjs-big-play-button {
  font-size: 3em;
  width: 3em;
  height: 1.5em;
  line-height: 1.5em;
  border: 0.06666em solid var(--terziary-btn-color);
  border-radius: 0.3em;
  left: 50%;
  top: 50%;
  margin-left: -1.5em;
  margin-top: -0.75em;
}

// Ensure control bar visibility
.vjs-default-skin .vjs-control-bar {
  display: flex !important;
  opacity: 1 !important;
  visibility: visible !important;
  min-height: 40px !important;
}

// Additional VideoJS background overrides
.video-js,
.video-js.vjs-default-skin,
.vjs-default-skin .vjs-tech {
  background-color: transparent !important;
  background: transparent !important;
}

// Override any poster background
.video-js .vjs-poster {
  background-color: transparent !important;
}

// Ensure no black backgrounds on any VideoJS elements
.video-js * {
  background-color: inherit;
}
</style>
